import React from 'react'
import { FormattedMessage } from 'react-intl'
import isToday from 'date-fns/isToday'
import { formatDate, formatTime } from '../../utils/format-date'
import DateFormats from '../../constants/DateFormats'
import { Cycle as CycleEntity } from '../../entities/cycle/Cycle'
import TimeStatus from '../time-status/TimeStatus'

import { styled } from '@mui/material/styles'
import messages from './CycleHeader.messages'
import { useSpring, animated } from 'react-spring'
import AcceptedTime from '../accepted-time/AcceptedTime'

export interface IOwnProps {
    cycle: CycleEntity
    className?: string
    active: boolean
    extraLabeledProductAmount?: number
}

export type IProps = IOwnProps

export interface TimeProps {
    active: boolean
}

const Time = styled('div', {
    shouldForwardProp: (prop) => prop !== 'active',
})<TimeProps>(({ theme, active }) => ({
    fontSize: 13,
    fontWeight: 400,
    color: theme.palette.blackColor.light,
    ...(active && {
        fontSize: 20,
        fontWeight: 600,
    }),
    '& div': {
        display: 'inline-block',
        marginRight: theme.spacing(),
        fontWeight: 500,
        color: theme.palette.text.secondary,
    },
})) as React.ComponentType<TimeProps>

const Date = styled('div')({
    fontSize: 13,
})

const OrderCount = styled('div')(({ theme }) => ({
    fontSize: 13,
    color: theme.palette.blackColor.light,
}))

export interface OrderProgressCountProps {
    active: boolean
}

const AnimatedOrderProgressCount = styled(animated.div, {
    shouldForwardProp: (prop) => prop !== 'active',
})<OrderProgressCountProps>(({ active }) => ({
    fontSize: 20,
    textAlign: 'center',
    ...(active && {
        fontSize: 32,
    }),
}))

const OrderProgressCountLabel = styled('div')(({ theme }) => ({
    fontSize: 12,
    color: theme.palette.greyColor.main,
}))

interface IExtraProgress {
    currentProgress: number
    extraProgress: number
}

const AnimatedExtraProgress = styled(animated.span, {
    shouldForwardProp: (prop) =>
        prop !== 'currentProgress' && prop !== 'extraProgress',
})<IExtraProgress>(({ theme, currentProgress, extraProgress }) => ({
    position: 'absolute',
    left: `calc(${currentProgress}% + 1px)`,
    top: 0,
    bottom: 0,
    width: `calc(${extraProgress}% - 1px)`,
    backgroundColor: theme.palette.secondary.main,
    border: '1px dashed rgba(0, 0, 0, 0.44)',
    zIndex: -1,
}))

const AnimatedCount = styled(
    animated.span,
    {}
)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: 15,
    lineHeight: 1.5,
    color: theme.palette.whiteColor.main,
    borderRadius: 10,
    backgroundColor: theme.palette.backgroundColor.main,
    padding: '0 4px',
}))

const CycleHeader: React.FC<IProps> = (props) => {
    const { cycle, className, active, extraLabeledProductAmount = 0 } = props

    const extraProgress =
        (extraLabeledProductAmount / cycle.productsAmount) * 100

    const [oldProductsAmount, setOldProductsAmount] = React.useState(
        cycle.productsAmount
    )
    const productsAmountChange = cycle.productsAmount - oldProductsAmount

    const [styles] = useSpring(
        {
            from: {
                opacity: 0.5,
            },
            to: {
                opacity: 1,
            },
            loop: {
                reverse: true,
            },
            config: {
                duration: 600,
            },
        },
        []
    )

    const [countUpdateSpring] = useSpring(
        {
            reset: true,
            from: {
                opacity: 1,
                top: 0,
            },
            to: {
                opacity: 0,
                top: -70,
            },
            config: {
                duration: 700,
            },
            onResolve: () => setOldProductsAmount(cycle.productsAmount),
        },
        [cycle.productsAmount]
    )

    return (
        <div className={className}>
            <div>
                <Date>
                    {isToday(cycle.pickupFrom) ? (
                        <FormattedMessage
                            {...messages.today}
                            values={{
                                date: formatDate(
                                    cycle.pickupFrom,
                                    DateFormats.DAY_WITH_SHORT_MONTH
                                ),
                            }}
                        />
                    ) : (
                        formatDate(
                            cycle.pickupFrom,
                            DateFormats.DAY_WITH_SHORT_MONTH
                        )
                    )}
                </Date>
                <Time active={active}>
                    <div>{formatTime(cycle.pickupFrom)}</div>
                    {cycle.isInProgress && (
                        <TimeStatus
                            time={cycle.pickupTo}
                            pickupX={cycle.pickupX}
                        />
                    )}
                    {cycle.isInIncoming && (
                        <AcceptedTime
                            acceptedDeadline={cycle.acceptanceDeadline}
                        />
                    )}
                </Time>
            </div>
            {cycle.isInIncoming && !active && (
                <OrderCount>
                    <FormattedMessage
                        {...messages.count}
                        values={{
                            count: cycle.orders.length,
                        }}
                    />
                </OrderCount>
            )}

            {cycle.isInProgress && (
                <AnimatedOrderProgressCount
                    active={active}
                    style={extraProgress > 0 ? styles : undefined}
                >
                    {productsAmountChange > 0 && (
                        <AnimatedCount style={countUpdateSpring}>
                            {productsAmountChange > 0
                                ? `+${productsAmountChange}`
                                : `-${productsAmountChange}`}
                        </AnimatedCount>
                    )}
                    {cycle.totalNumberProductsLabeled}/{cycle.numberOfProducts}
                    {active && (
                        <OrderProgressCountLabel>
                            <FormattedMessage {...messages.labeledItems} />
                        </OrderProgressCountLabel>
                    )}
                </AnimatedOrderProgressCount>
            )}

            {extraProgress > 0 && (
                <AnimatedExtraProgress
                    currentProgress={cycle.progress}
                    extraProgress={extraProgress}
                    style={styles}
                />
            )}
        </div>
    )
}

export interface CycleHeaderProps {
    width?: number
}

export default styled(CycleHeader)<CycleHeaderProps>(
    ({ theme, cycle, active }) => ({
        position: 'relative',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2),
        color: theme.palette.blackColor.light,
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: `${100 - cycle.progress}% `,
            bottom: 0,
            left: 0,
            backgroundColor: theme.palette.secondary.main,
            pointerEvents: 'none',
            transition: 'right .3s ease',
        },
        '& div': {
            position: 'relative',
            zIndex: 1,
        },
        paddingRight: theme.spacing(9),
        ...(cycle.isInProgress &&
            active && {
                paddingRight: theme.spacing(5),
            }),
    })
)
